import React, { useRef } from 'react';
import {
  Avatar,
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { NS, PROFILE_KEYS } from '../../../i18n/constants/keys';
import { toast } from '../../../ui/toast';
import { SectionCard } from '../../../ui/mui';
import { CAREER_STATUS_COLOR_MAP, CareerStatus } from '../constants';
import { getCareerStatusLabel } from '../functions';

interface ICandidateSectionProps {
  careerStatus: CareerStatus;
  onCareerStatusChange: (status: CareerStatus) => void;
}

type ResumeAction = 'preview' | 'download' | 'delete';

export const CandidateSection = ({ careerStatus, onCareerStatusChange }: ICandidateSectionProps) => {
  const { t } = useTranslation(NS.profile);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleResumeAction = (action: ResumeAction) => (): void => {
    if (action === 'delete') {
      toast.warn(t(PROFILE_KEYS.messages.resumeDeleted));
    } else if (action === 'download') {
      toast.info(t(PROFILE_KEYS.messages.resumeDownloading));
    } else {
      toast.info(t(PROFILE_KEYS.messages.resumePreview));
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <SectionCard
        title={t(PROFILE_KEYS.sections.candidate.title)}
        subtitle={t(PROFILE_KEYS.sections.candidate.intro)}
      >
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              {t(PROFILE_KEYS.sections.candidate.jobSeekingStatus)}
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>{t(PROFILE_KEYS.sections.candidate.jobSeekingStatus)}</InputLabel>
              <Select
                value={careerStatus}
                label={t(PROFILE_KEYS.sections.candidate.jobSeekingStatus)}
                onChange={(e) => onCareerStatusChange(e.target.value as CareerStatus)}
              >
                {Object.values(CareerStatus).map((status) => (
                  <MenuItem key={status} value={status}>
                    {getCareerStatusLabel(t, status)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ mt: 1.5 }}>
              <Chip
                label={getCareerStatusLabel(t, careerStatus)}
                size="small"
                color={CAREER_STATUS_COLOR_MAP[careerStatus]}
                variant="filled"
                sx={{ fontWeight: 600 }}
              />
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2">
              {t(PROFILE_KEYS.sections.candidate.resume)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t(PROFILE_KEYS.sections.candidate.resumeHint)}
            </Typography>

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx"
              hidden
              onChange={() => toast.info(t(PROFILE_KEYS.messages.resumePreview))}
            />
            <Box
              role="button"
              tabIndex={0}
              aria-label={t(PROFILE_KEYS.sections.candidate.resume)}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
              }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                width: '100%',
                minHeight: 144,
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 3,
                bgcolor: 'background.paper',
                cursor: 'pointer',
                p: 3,
                mb: 2,
                transition: 'all 200ms ease',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
                '&:focus-visible': { outline: 2, outlineColor: 'primary.main' },
              }}
            >
              <CloudUploadIcon color="disabled" fontSize="large" />
              <Typography variant="body2">
                <Typography component="span" variant="body2" color="primary" fontWeight={600}>
                  {t(PROFILE_KEYS.sections.candidate.uploadClick)}
                </Typography>{' '}
                {t(PROFILE_KEYS.sections.candidate.uploadOrDrag)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t(PROFILE_KEYS.sections.candidate.uploadFormats)}
              </Typography>
            </Box>

            <Paper
              variant="outlined"
              sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                <Avatar variant="rounded" sx={{ bgcolor: 'error.light', color: 'error.main' }}>
                  <DescriptionIcon fontSize="small" />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" noWrap>
                    {t(PROFILE_KEYS.sections.candidate.resumeFileName)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t(PROFILE_KEYS.sections.candidate.resumeMeta)}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title={t(PROFILE_KEYS.sections.candidate.viewResume)}>
                  <IconButton aria-label={t(PROFILE_KEYS.sections.candidate.viewResume)} size="small" onClick={handleResumeAction('preview')} sx={{ minWidth: 44, minHeight: 44 }}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t(PROFILE_KEYS.sections.candidate.downloadResume)}>
                  <IconButton aria-label={t(PROFILE_KEYS.sections.candidate.downloadResume)} size="small" onClick={handleResumeAction('download')} sx={{ minWidth: 44, minHeight: 44 }}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t(PROFILE_KEYS.sections.candidate.deleteResume)}>
                  <IconButton aria-label={t(PROFILE_KEYS.sections.candidate.deleteResume)} size="small" color="error" onClick={handleResumeAction('delete')} sx={{ minWidth: 44, minHeight: 44 }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Paper>
          </Box>
        </Stack>
      </SectionCard>
    </Box>
  );
};
